import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import lodashGet from "lodash/get";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";

import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";

// import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";

//icons
import DownloadIcon from "mdi-material-ui/Download";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// jaza-soft
import { PageFooter, SaveButton, TextInput, FileInput, FileField, SimpleForm, showNotification, required } from "jazasoft";

// local

import MuiTable from "../../../components/MuiTable";
import ExcelErrorDialog from "../../../components/ExcelErrorDialog";

import useQuery from "../../../hooks/useQuery";

import useSafeState from "../../../hooks/useSafeState";
import useMutation from "../../../hooks/useMutation";
import { getDistinctObjectValues, getDistinctValues, isEmpty, toFixed } from "../../../utils/helpers";
import { useDeepMemo, useEntityPrivilege, useSetting } from "../../../hooks";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { UOM } from "../../../constants";
import { REGEX_POSITIVE_DECIMAL } from "../../../utils/regex";
import SettingKeys from "../../../utils/SettingKeys";
import { Entity } from "../../../utils/types";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const rollPredefinedFields = ["supplierRollNo", "factoryRollNo", "length", "weight", "width", "gsm", "rackNo", "blanketQty"];

const useStyles = makeStyles(theme => ({
  content: {
    margin: theme.spacing(3),
    marginTop: "0.5em"
  },
  button: {
    margin: "0 1em"
  },
  marginVertical: {
    marginTop: "1em",
    marginBottom: "2em"
  },
  pageFooter: {
    "&>*": {
      marginRight: "1em"
    }
  },
  drawerHeader: {
    display: "flex",
    width: 800,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  }
}));

const FormFooter = props => (
  <PageFooter>
    <SaveButton label="Upload" {...props} />
  </PageFooter>
);

const PackingListDrawer = props => {
  const classes = useStyles();
  const { roles, hasAccess, open, invoice, fabricItem, onDrawerClose, onRefresh } = props;

  const { disableDelete } = useEntityPrivilege("GRN");
  const dispatch = useDispatch();
  const i18nMessages = useSelector(state => state?.i18n?.messages);

  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ROLLS);

  const [errorList, setErrorList] = useSafeState([]);

  const [mutate] = useMutation();
  const [download] = useMutation({ rawResponse: true });

  const children = invoice?.fabricItemList?.filter(fi => fi.parentId === fabricItem?.id) || [];
  const fabItemIds = !isEmpty(fabricItem?.parentId) || isEmpty(children) ? [fabricItem?.id] : children.map(e => e.id);

  const { data: rollInfoList = [] } = useQuery(!isEmpty(fabricItem) && { url: `rolls`, params: { search: `fabricItem.id=in=(${fabItemIds})`, size: 2000 } });
  const rollList = getDistinctObjectValues(rollInfoList.map(e => e.supplierRoll));

  //** Supplier Rolls Upload against Fabric Item */
  const onUpload = values => {
    const { grn, file } = values;
    var data = new FormData();
    data.append("file", file && file.rawFile);
    const options = { url: `import/excel`, method: "post", data };
    mutate(options, {
      onSuccess: excelSheetList => {
        if (Array.isArray(excelSheetList) && excelSheetList.length > 0) {
          const excelRowList = excelSheetList[0].excelData?.rowList || [];

          const headerRow = excelRowList[0];
          const headerMap = headerRow.cellList.reduce((acc, c) => ({ ...acc, [`${c.colNo}`]: c.value || "" }), {});

          const rollList = excelRowList
            .slice(1)
            .map(row => row.cellList.reduce((acc, c) => ({ ...acc, [headerMap[`${c.colNo}`].toUpperCase()]: c.value }), {}))
            .map((roll, idx) => {
              return rollPredefinedFields.reduce(
                (acc, field) => ({
                  ...acc,
                  [field]: roll[lodashGet(i18nMessages, `resources.rolls.fields.${field}`, field).toUpperCase()]
                }),
                { rowNo: idx + 2 }
              );
            })
            .map(({ supplierRollNo, length, width, ...roll }) => ({ ...roll, rollNo: supplierRollNo, supplierLength: length, supplierWidth: width }))
            .filter(roll => !isEmpty(roll.rollNo));

          if (isEmpty(rollList)) {
            dispatch(showNotification("Roll Upload Failed. Check if Roll Upload Excel Template is Proper.", { type: "danger" }));
          } else {
            const errors = rollList.flatMap((roll, idx) => {
              let errList = [];
              if (fabricItem?.uomLength === UOM.KILOGRAM.value) {
                if (isEmpty(roll.weight)) {
                  errList.push({
                    column: lodashGet(i18nMessages, `resources.rolls.fields.weight`, "weight").toUpperCase(),
                    row: roll.rowNo,
                    message: "Roll Weight is required"
                  });
                } else if (!REGEX_POSITIVE_DECIMAL.test(roll.weight)) {
                  errList.push({
                    column: lodashGet(i18nMessages, `resources.rolls.fields.weight`, "weight").toUpperCase(),
                    row: roll.rowNo,
                    message: "Roll Weight must be positive number"
                  });
                }
              } else {
                if (isEmpty(roll.supplierLength)) {
                  errList.push({
                    column: lodashGet(i18nMessages, `resources.rolls.fields.length`, "length").toUpperCase(),
                    row: roll.rowNo,
                    message: "Roll Length is required"
                  });
                } else if (!REGEX_POSITIVE_DECIMAL.test(roll.supplierLength)) {
                  errList.push({
                    column: lodashGet(i18nMessages, `resources.rolls.fields.length`, "length").toUpperCase(),
                    row: roll.rowNo,
                    message: "Roll Length must be positive number"
                  });
                }
              }
              if (!isEmpty(roll.supplierWidth) && !REGEX_POSITIVE_DECIMAL.test(roll.supplierWidth)) {
                errList.push({
                  column: lodashGet(i18nMessages, `resources.rolls.fields.width`, "width").toUpperCase(),
                  row: roll.rowNo,
                  message: "Roll Width must be positive number"
                });
              }
              if (!isEmpty(roll.gsm) && !REGEX_POSITIVE_DECIMAL.test(roll.gsm)) {
                errList.push({
                  column: lodashGet(i18nMessages, `resources.rolls.fields.gsm`, "gsm").toUpperCase(),
                  row: roll.rowNo,
                  message: "Roll GSM must be positive number"
                });
              }

              return errList;
            });

            if (!isEmpty(errors)) {
              setErrorList(errors);
            } else {
              const options = {
                url: `invoices/${invoice?.id}/fabric-items/${fabricItem?.id}/rolls`,
                method: "post",
                params: { grn },
                data: rollList
              };

              mutate(options, {
                onSuccess: () => {
                  dispatch(showNotification("Packing List uploaded successfully"));
                  onRefresh && onRefresh();
                  onDrawerClose();
                },
                onFailure: err => {
                  if (err.status === 400 && err.errors && Array.isArray(err.errors)) {
                    setErrorList(err.errors);
                  }
                }
              });
            }
          }
        }
      }
    });
  };

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "supplierRollNo", title: "Supplier \nRoll No" },
      { dataKey: "factoryRollNo", title: "Factory \nRoll No", render: value => value || "-" },
      {
        dataKey: "supplierLength",
        title: "Supplier Length",
        align: "center",
        render: value => `${Number(toFixed(value))} ${UomCodeFromValue[fabricItem.uomLength]}`
      },
      {
        dataKey: "weight",
        title: "Weight",
        render: (value, _, row) => `${Number(toFixed(value))} ${UomCodeFromValue[fabricItem.uomLength]}`
        // render: (value, _, row) => `${Number(toFixed(value))} ${UomCodeFromValue[fabricItem.uomLength]} (${toFixed(row.length)} m)`
      },
      {
        dataKey: "rollForm",
        title: "Roll Form",
        render: () => fabricItem?.rollForm || "-"
      },
      {
        dataKey: "supplierWidth",
        title: "Finished \nWidth",
        render: value => (isEmpty(value) ? "-" : `${Number(toFixed(value))} ${UomCodeFromValue[fabricItem.uomWidth]}`)
      },
      { dataKey: "gsm", title: "GSM", render: value => value || "-" },
      {
        dataKey: "blanketQty",
        title: "Blanket Issuance",
        render: (value, _, row) => `${Number(toFixed(value))} ${UomCodeFromValue[fabricItem.uomLength]}`
        // render: (value, _, row) =>
        //   `${Number(toFixed(value))} ${UomCodeFromValue[fabricItem.uomLength]}` +
        //   (fabricItem.uomLength !== UOM.METER.value ? ` (${toFixed(row.blanketQtyInMeter)} m)` : "")
      },
      {
        dataKey: "available",
        title: "Available",
        align: "center",
        render: (value, _, row) =>
          `${
            fabricItem?.uomLength === UOM.KILOGRAM.value
              ? `${toFixed(row.weight - row.blanketQty)} kg`
              : `${toFixed(row.supplierLength - row.blanketQty)} ${UomCodeFromValue[fabricItem.uomLength]}`
          }`
        // render: (value, _, row) =>
        //   `${
        //     fabricItem?.uomLength === UOM.KILOGRAM.value
        //       ? `${toFixed(row.weight - row.blanketQty)} kg (${toFixed(row.length - row.blanketQtyInMeter)} m)`
        //       : `${toFixed(row.supplierLength - row.blanketQty)} ${UomCodeFromValue[fabricItem.uomLength]}` +
        //         (fabricItem.uomLength !== UOM.METER.value ? ` (${toFixed(row.length - row.blanketQtyInMeter)} m)` : "")
        //   }`
      }
    ];
    if (fabricItem?.uomLength === UOM.KILOGRAM.value) {
      cols = cols.filter(c => c.dataKey !== "supplierLength");
    } else {
      cols = cols.filter(c => c.dataKey !== "weight");
    }
    return cols;
  }, [fabricItem]);

  const rows = useDeepMemo(() => {
    if (isEmpty(rollList)) return [];
    const supplierRollIds = getDistinctValues(rollInfoList?.map(e => e.supplierRoll?.id) || []);
    return rollList.filter(e => supplierRollIds.includes(e.id));
  }, [rollList, rollInfoList, fabricItem]);

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "download") {
      const filename = `PL_${invoice.invoiceNo}_${fabricItem?.fabric?.itemCode}_${fabricItem?.color}`.replaceAll(" ", "-").replaceAll("/", "-");

      const columnList = rollFields
        .filter(field => rollPredefinedFields.includes(field))
        .map(field => ({
          dataKey: field,
          type: ["length", "weight", "width", "gsm"].includes(field) ? "double" : null,
          title: lodashGet(i18nMessages, `resources.rolls.fields.${field}`, field).toUpperCase()
        }));

      const rowList = rollList
        .map(roll => ({ ...roll, supplierRollNo: roll.rollNo, width: roll.supplierWidth, length: roll.supplierLength }))
        .map((row, idx) => ({
          rowNo: idx + 1,
          cellList: columnList.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] }))
        }));

      const options = {
        url: `export/excel`,
        method: "post",
        responseType: "arraybuffer",
        params: { filename },
        data: [{ sheetNo: 0, sheetName: "ROLLS", excelData: { columnList, rowList } }]
      };

      download(options, {
        onSuccess: response => {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
          }
          onActionComplete && onActionComplete(rows);
        }
      });
    } else if (action === "delete") {
      const options = {
        url: `invoices/${invoice.id}/fabric-items/${fabricItem.id}/rolls`,
        method: "delete"
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("Packing List deleted successfully"));
          onDrawerClose();
          onRefresh && onRefresh();
        }
      });
    }
  };

  let footerActions = [
    {
      name: "download",
      tooltip: "Download",
      icon: <DownloadIcon />,
      options: { variant: "contained", color: "primary", style: { margin: "0.5em" } }
    }
  ];
  if (hasPrivilege(roles, hasAccess, ResourceId.GRN, Scope.DELETE) && !disableDelete) {
    footerActions.push({
      name: "delete",
      tooltip: "Delete",
      icon: <DeleteIcon />,
      options: { variant: "outlined", style: { margin: "0.5em", color: "red", borderColor: "red" } }
    });
  }

  return (
    <div>
      <ExcelErrorDialog maxWidth="xl" open={errorList?.length > 0 || false} errors={errorList} onClose={() => setErrorList(null)} />

      {/** Drawer For Supplier Rolls Upload Against Fabric Item */}
      <Drawer anchor="right" open={open} onClose={onDrawerClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
          <div>
            <Typography variant="h5">{fabricItem?.rollCount > 0 ? "" : "Upload"} Packing List</Typography>
          </div>
        </div>
        <Divider />
        {(isEmpty(fabricItem?.rollCount) || fabricItem?.rollCount === 0) && (
          <SimpleForm record={{ grn: fabricItem?.grn }} style={{ margin: "1em" }} onSubmit={onUpload} footer={<FormFooter />}>
            <TextInput source="grn" label="GRN" validate={[required()]} disabled={!isEmpty(fabricItem?.grn)} />
            <FileInput
              source="file"
              label="Packing List File"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              xs={12}
              fullWidth
              validate={[required()]}>
              <FileField source="src" title="title" />
            </FileInput>
          </SimpleForm>
        )}
        {fabricItem?.rollCount > 0 && (
          <MuiTable
            title="Packing List"
            columns={columns}
            rows={rows}
            searchable
            searchKeys={["rollNo", "factoryRollNo"]}
            pageable
            pageSize={15}
            rowsPerPageOptions={[15, 25]}
            footerActions={footerActions}
            onFooterActionClick={onFooterActionClick}
            i18nKey="resources.rolls.fields"
          />
        )}
      </Drawer>
    </div>
  );
};

export default PackingListDrawer;
